import {
  Button,
  ButtonSize,
  Color,
  Divider,
  Flex,
  FormItemsGroup,
  GridTemplateValue,
  Space,
  Spacing,
  Tag,
  useModal,
} from '@billy/design-system'
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { RecurringInvoiceDelivery } from '@features/recurringInvoices/enums/recurringInvoiceDelivery'
import { RecurringInvoiceDuration } from '@features/recurringInvoices/enums/recurringInvoiceDuration'

import { ModalId } from '../../../../../../../../../../enums/modalId'
import { useRecurringInvoiceForm } from '../../../../../../contexts/recurringInvoiceFormContext'
import { Delivery } from './elements/Delivery'
import { DeliveryNotification } from './elements/DeliveryNotification'
import { Duration } from './elements/Duration'
import { FirstInvoiceDate } from './elements/FirstInvoiceDate'
import { Frequency } from './elements/Frequency'
import { Occurrences } from './elements/Occurrences'
import { RecurringInvoiceEmailTemplateModal } from './elements/RecurringInvoiceEmailTemplateModal'
import * as Styled from './styles'

const FOOTER_BUTTON_SIZE: ButtonSize = 'sm'

// fixed size for the Occurrences number field
const ITEMS_TEMPLATE: GridTemplateValue[] = [1, 1, 1, '95px', 1]

export const RecurringInvoiceSettings = (): ReactElement => {
  const { t } = useTranslation()
  const { isOpen: isEmailTemplateModalOpen, open: openRecurringInvoiceEmailTemplateModal } = useModal(
    ModalId.RecurringInvoiceEditEmailTemplateModal,
  )
  const { recurringInvoiceForm } = useRecurringInvoiceForm()

  const {
    formState: { submitCount },
    trigger,
    watch,
  } = recurringInvoiceForm
  const [duration, delivery] = watch(['duration', 'delivery'])
  const shouldShowEmailTemplateModalTrigger = delivery === RecurringInvoiceDelivery.CreateAndSend
  const shouldShowOccurrences = duration === RecurringInvoiceDuration.Repeat
  const groupProps = shouldShowOccurrences ? { itemsTemplate: ITEMS_TEMPLATE } : { columnSpacing: Spacing.S16 }

  const [missingEmailTemplateDataMessage, setMissingEmailTemplateDataMessage] = useState<string>()
  const isFirstRenderRef = useRef(true)

  // Only refresh this once the email template modal is closed (Save or Cancel) or the whole
  // recurring invoice form gets submitted - not on every keystroke inside the still-open modal,
  // whose own fields already validate live.
  useEffect(() => {
    const isFirstRender = isFirstRenderRef.current
    isFirstRenderRef.current = false

    if (!isFirstRender && !isEmailTemplateModalOpen) {
      trigger(['message', 'message.to', 'message.body']).then(() => {
        setMissingEmailTemplateDataMessage(recurringInvoiceForm.formState.errors.message?.message)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmailTemplateModalOpen, submitCount])

  const hasMissingEmailTemplateData = shouldShowEmailTemplateModalTrigger && !!missingEmailTemplateDataMessage

  const handleEditEmailTemplateClick = useCallback(() => {
    openRecurringInvoiceEmailTemplateModal()
  }, [openRecurringInvoiceEmailTemplateModal])

  return (
    <>
      <Styled.RecurringInvoiceSettingsWrapper>
        <Flex width="100%" flexDirection="column">
          <FormItemsGroup itemsInRow={4} {...groupProps}>
            <FirstInvoiceDate />
            <Frequency />
            <Duration />
            {shouldShowOccurrences ? <Occurrences /> : null}
            <Delivery />
          </FormItemsGroup>
          <Space size="s16" />
          <Divider dividerColor={Color.Grey200} />
          <Space size="s10" />
          <Styled.RecurringInvoiceSettingsFooter buttonSize={FOOTER_BUTTON_SIZE}>
            <DeliveryNotification />
            {shouldShowEmailTemplateModalTrigger && (
              <Flex alignItems="center">
                {hasMissingEmailTemplateData && (
                  <Tag color="red" size="xs" type="subtle">
                    {missingEmailTemplateDataMessage}
                  </Tag>
                )}
                <Button
                  icon="squarePen"
                  size={FOOTER_BUTTON_SIZE}
                  variant="text"
                  onClick={handleEditEmailTemplateClick}
                >
                  {t('external_invoices.editor.form.recurring_invoice_settings.email_template.button.label')}
                </Button>
              </Flex>
            )}
          </Styled.RecurringInvoiceSettingsFooter>
        </Flex>
      </Styled.RecurringInvoiceSettingsWrapper>
      <RecurringInvoiceEmailTemplateModal id={ModalId.RecurringInvoiceEditEmailTemplateModal} />
    </>
  )
}
